import { useOrders } from "@/hooks/useOrders";
import { Fragment as FragmentRows, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Search } from "lucide-react";
import type { Account, Backtest, OrderRecord, OrderStats } from "@/api/types";
import { useApi } from "@/context/ApiContext";
import { useFormatDate } from "@/hooks/useFormatDate";
import { ExchangeMark, Pill, type Tone } from "@/components/ui/kit";
import { humanError, orderReason, pendingAge } from "@/lib/order-labels";
import { matchRoundTrips, realizedPnlByOrder, unrealizedTripPnl, type RoundTrip } from "@/lib/order-trades";
import { approxQty, num, orderLabel, pnlColor as orderPnlColor, qtyOf, signedUsd } from "@/lib/order-format";
import { OrderDetailsDialog } from "@/components/OrderDetailsDialog";
import { Pager } from "@/components/ui/Pager";
import { clampPage } from "@/lib/paging";
import { groupByPeriod, type Period } from "@/lib/period-groups";

type Scope = "account" | "user";

type Props = {
  /** Single-account mode (default). */
  accountId?: string;
  scope?: Scope;
  backtests?: Backtest[];
  /** Pre-filter to one backtest (no backtest dropdown). */
  fixedBacktestId?: string;
  /** Hide ledger chrome (title, description, refresh); taller rows for Live Trades split view. */
  compact?: boolean;
  /** Card title when compact (paired with status filter in one header row); "" hides it. */
  title?: string;
  /**
   * All live trades (Live → Trades): accounts as chips with their order counts,
   * a search box, rows that open their strategy, and "Load more".
   */
  liveTrades?: boolean;
};

const PAGE = 200;
/** Rows per page in the table. */
const ROWS = 10;

function fmt(n: string | undefined | null, digits = 8): string {
  if (n == null || n === "") return "—";
  const num = parseFloat(n);
  if (Number.isNaN(num)) return String(n);
  if (num === 0) return "0";
  return num.toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  });
}

function fmtSigned(n: string | undefined | null, digits = 2): string {
  if (n == null || n === "") return "—";
  const num = parseFloat(n);
  if (Number.isNaN(num)) return String(n);
  const s = num.toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  });
  return num > 0 ? `+${s}` : s;
}

function fmtPct(x: number | null | undefined, digits = 1): string {
  if (x == null || Number.isNaN(x)) return "—";
  return `${(x * 100).toFixed(digits)}%`;
}

function pnlClass(n: string | undefined | null): string {
  if (n == null || n === "") return "";
  const num = parseFloat(n);
  if (Number.isNaN(num) || num === 0) return "";
  return num > 0 ? "text-[var(--trading-profit)]" : "text-[var(--trading-loss)]";
}

function normalizeStats(s: OrderStats): OrderStats {
  return {
    ...s,
    capital: s.capital
      ? { ...s.capital, open: s.capital.open ?? [] }
      : { capital_years: 0, air: null, max_deployed: "0", open: [] },
    groups: (s.groups ?? []).map((g) => ({ ...g, open: g.open ?? [], orders: g.orders })),
    skip_reasons: s.skip_reasons ?? [],
    error_reasons: s.error_reasons ?? [],
  };
}

const STATUS_PILL: Record<string, { tone: Tone; label: string }> = {
  done: { tone: "ok", label: "Filled" },
  error: { tone: "bad", label: "Failed" },
  skipped: { tone: "warn", label: "Skipped" },
  pending: { tone: "info", label: "Pending" },
  canceled: { tone: "mute", label: "Canceled" },
};

type StatusChip = "" | "done" | "problems" | "pending" | "canceled";

function matchesChip(status: string, chip: StatusChip): boolean {
  if (!chip) return true;
  if (chip === "problems") return status === "error" || status === "skipped";
  return status === chip;
}

type TripChip = "" | "win" | "loss" | "open";

/** "4h 17m", "9m", "2d 3h". */
function held(from: string | null, to: string | null, now: number): string {
  if (!from) return "";
  const ms = (to ? Date.parse(to) : now) - Date.parse(from);
  if (!Number.isFinite(ms) || ms < 0) return "";
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${Math.max(1, m)}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

const money2 = (v: number) =>
  `${v > 0 ? "+" : v < 0 ? "−" : ""}$${Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pnlColor = (v: number | null | undefined) =>
  v == null || v === 0 ? "var(--text-muted)" : v > 0 ? "var(--success)" : "var(--danger)";

const seg = (on: boolean) =>
  `rounded-[7px] px-2.5 py-0.5 text-xs ${on ? "bg-[var(--primary-soft)] font-medium text-[var(--primary)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`;
const chipCls = (on: boolean) =>
  `rounded-full border border-[var(--border)] px-2.5 py-[3px] text-xs ${on ? "bg-[var(--bg-card-elevated)] text-[var(--text-primary)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`;
const dayRow = "border-b border-[var(--divider)] bg-[var(--bg-secondary)] px-3 py-1.5 text-[11px] text-[var(--text-muted)]";
const tag = "whitespace-nowrap rounded-[6px] border border-[var(--border)] px-1.5 py-px text-[11px] text-[var(--text-muted)]";

const th = "whitespace-nowrap border-b border-[var(--border)] px-3 py-2 text-left text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--text-muted)]";
const td = "whitespace-nowrap border-b border-[var(--divider)] px-3 py-[9px]";

export function OrderLedger({
  accountId = "",
  scope = "account",
  backtests: backtestsProp,
  fixedBacktestId = "",
  compact = false,
  title,
  liveTrades = false,
}: Props) {
  const api = useApi(["accounts", "backtests"]);
  const navigate = useNavigate();
  const { dateTime, dateTimeShort, mode: tzMode } = useFormatDate();
  const utc = tzMode === "utc";
  const userScope = scope === "user";
  const backtests = backtestsProp ?? api.backtests;

  const [accountFilter, setAccountFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusChip>("");
  const [backtestFilter, setBacktestFilter] = useState(fixedBacktestId);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [statsError, setStatsError] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  const [limit, setLimit] = useState(PAGE);
  /** Trades (entry and exit matched) or the raw orders. */
  const [view, setView] = useState<"trades" | "orders">(liveTrades ? "trades" : "orders");
  /** Rows are one flat list unless the user asks for day or week groups. */
  const [period, setPeriod] = useState<Period | "none">("none");
  const [tripChip, setTripChip] = useState<TripChip>("");
  const [symbolFilter, setSymbolFilter] = useState("");
  const [now] = useState(() => Date.now());
  /** The banner's "Show them" lists the failed orders; this is where "Hide them" returns to. */
  const beforeProblems = useRef<{ view: "trades" | "orders"; status: StatusChip }>({ view: liveTrades ? "trades" : "orders", status: "" });
  const showingProblems = view === "orders" && statusFilter === "problems";
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  /** Orders per account (and "" for all), for the account chips. */
  const [accountCounts, setAccountCounts] = useState<Record<string, number>>({});
  const statsSeq = useRef(0);

  const statsAccount = userScope ? accountFilter : accountId;
  const statsBacktest = backtestFilter !== "none" ? fixedBacktestId || backtestFilter : "";
  const subscription = useOrders({ account_id: statsAccount, backtest_id: statsBacktest, limit: userScope ? limit : 200 }, api.ready && (userScope || !!accountId));
  const orders = subscription.rows;
  const loading = subscription.status === 'idle' || subscription.status === 'loading';
  const error = subscription.error || '';
  const load = subscription.refresh;

  const loadStats = useCallback(async () => {
    const seq = ++statsSeq.current;
    setStatsError("");
    try {
      const s =
        (await api.commands.getOrderStats({
          account_id: statsAccount || "",
          backtest_id: statsBacktest,
        })) ?? null;
      if (seq !== statsSeq.current) return;
      setStats(s ? normalizeStats(s as OrderStats) : null);
    } catch (e: unknown) {
      if (seq === statsSeq.current) {
        setStats(null);
        setStatsError((e instanceof Error && e.message) || "Failed to load stats");
      }
    }
  }, [api.commands, statsAccount, statsBacktest]);

  useEffect(() => { setBacktestFilter(fixedBacktestId); }, [accountId, fixedBacktestId]);
  useEffect(() => { if (api.ready && (userScope || accountId)) void loadStats(); }, [api.ready, userScope, accountId, loadStats, orders]);

  // Account chips show how many orders each account has (from its stats).
  const accountIds = api.accounts.map((a: { id: string }) => a.id).join(",");
  useEffect(() => {
    if (!liveTrades || !api.ready) return;
    let cancelled = false;
    const ids = ["", ...(accountIds ? accountIds.split(",") : [])];
    void Promise.all(
      ids.map(async (id) => {
        try {
          const st = (await api.commands.getOrderStats({ account_id: id, backtest_id: "" })) as OrderStats | null;
          return [id, st?.health?.counts?.total ?? 0] as const;
        } catch {
          return null;
        }
      })
    ).then((rows) => {
      if (cancelled) return;
      const next: Record<string, number> = {};
      for (const r of rows) if (r) next[r[0]] = r[1];
      setAccountCounts(next);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveTrades, api.ready, accountIds]);

  /** Orders in scope before the status chip, so chip counts stay stable. */
  const scoped = useMemo(() => {
    return orders.filter((o) => {
      if (fixedBacktestId) return o.backtest_id === fixedBacktestId;
      if (backtestFilter === "none") return !o.backtest_id;
      if (backtestFilter && !userScope) return o.backtest_id === backtestFilter;
      return true;
    });
  }, [orders, userScope, backtestFilter, fixedBacktestId]);

  const backtestName = useMemo(
    () => new Map((api.backtests as Backtest[]).map((b) => [b.id, b.name])),
    [api.backtests]
  );
  /** Strategy of an order: its name, or why there is none. */
  const strategyLabel = (backtestId: string | null | undefined): { text: string; muted: boolean } => {
    if (!backtestId) return { text: "Manual", muted: true };
    const name = backtestName.get(backtestId);
    return name ? { text: name, muted: false } : { text: "Deleted strategy", muted: true };
  };
  const matchesText = (symbol: string, backtestId: string | null | undefined) => {
    if (symbolFilter && symbol !== symbolFilter) return false;
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    return symbol.toLowerCase().includes(needle) || (backtestId ? backtestName.get(backtestId) ?? "" : "").toLowerCase().includes(needle);
  };

  const filtered = useMemo(
    () => scoped.filter((o) => matchesChip(o.status, statusFilter) && matchesText(o.symbol, o.backtest_id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scoped, statusFilter, query, symbolFilter, backtestName]
  );

  // Each closing order's result, matched over every loaded order in scope.
  const pnlByOrder = useMemo(() => realizedPnlByOrder(scoped), [scoped]);

  // Trades: entries and exits matched over every loaded order in scope.
  const trips = useMemo(() => (view === "trades" ? matchRoundTrips(scoped) : []), [scoped, view]);
  const tripMatches = (t: RoundTrip, chip: TripChip) =>
    chip === "" ? true : chip === "open" ? t.open : !t.open && t.pnl != null && (chip === "win" ? t.pnl > 0 : t.pnl < 0);
  const tripsInScope = useMemo(
    () => trips.filter((t) => matchesText(t.symbol, t.backtestId)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trips, query, symbolFilter, backtestName]
  );
  const shownTrips = useMemo(() => tripsInScope.filter((t) => tripMatches(t, tripChip)), [tripsInScope, tripChip]);

  // Any change to what is listed starts again from the first page.
  useEffect(() => setPage(0), [view, period, statusFilter, tripChip, query, symbolFilter, backtestFilter, accountFilter]);
  const listed = view === "trades" ? shownTrips.length : filtered.length;
  const safePage = clampPage(page, ROWS, listed);
  const pageOrders = filtered.slice(safePage * ROWS, (safePage + 1) * ROWS);
  const pageTrips = shownTrips.slice(safePage * ROWS, (safePage + 1) * ROWS);

  // Orders that failed or were skipped in the last 7 days, with why.
  const recentProblems = useMemo(() => {
    const since = now - 7 * 86_400_000;
    const reasons = new Map<string, number>();
    let count = 0;
    for (const o of scoped) {
      if ((o.status !== "error" && o.status !== "skipped") || Date.parse(o.created_at) < since) continue;
      count++;
      const why = humanError(o.error) || orderReason(o.comment) || (o.status === "skipped" ? "Skipped" : "Failed");
      reasons.set(why, (reasons.get(why) || 0) + 1);
    }
    return { count, reasons: [...reasons].sort((a, b) => b[1] - a[1]).slice(0, 3) };
  }, [scoped, now]);

  const chipCount = (chip: StatusChip) => scoped.filter((o) => matchesChip(o.status, chip)).length;

  const accountById = useMemo(
    () => new Map(api.accounts.map((a: { id: string; name: string; provider: string }) => [a.id, a])),
    [api.accounts]
  );
  /** Strategies that still exist as live strategies: their rows open the strategy page. */
  const liveIds = useMemo(
    () => new Set(api.backtests.filter((b: Backtest) => b.metadata?.ui?.mode === "live").map((b: Backtest) => b.id)),
    [api.backtests]
  );

  const errorStats = useMemo(
    () =>
      stats
        ? [
            ...stats.skip_reasons.map((r) => ({ ...r, kind: "skipped" as const })),
            ...stats.error_reasons.map((r) => ({ ...r, kind: "error" as const })),
          ]
        : [],
    [stats]
  );

  function refresh() {
    void load();
    void loadStats();
  }

  const chips = (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Order status">
      {(
        [
          ["", "All"],
          ["done", "Filled"],
          ["problems", "Problems"],
          ["pending", "Pending"],
        ] as const
      ).map(([chip, label]) => {
        const n = chipCount(chip);
        if (chip && chip !== statusFilter && n === 0) return null;
        const on = statusFilter === chip;
        return (
          <button
            key={chip || "all"}
            type="button"
            aria-pressed={on}
            onClick={() => setStatusFilter(chip)}
            className={chipCls(on)}
          >
            {label} <span className="num">{n}</span>
          </button>
        );
      })}
    </div>
  );

  const stat = (label: ReactNode, value: ReactNode, sub?: ReactNode, cls = "") => (
    <div className="min-w-0 border-b border-r border-[var(--divider)] px-3.5 py-2.5">
      <div className="whitespace-nowrap text-[11px] text-[var(--text-muted)]">{label}</div>
      <div className={`num mt-0.5 text-[15px] font-semibold text-[var(--text-primary)] ${cls}`}>{value}</div>
      {sub && <div className="truncate text-[11px] text-[var(--text-muted)]">{sub}</div>}
    </div>
  );

  const statsStrip = stats ? (
    <div className="overflow-hidden rounded-[12px] border border-[var(--border)]">
      <div className="-mb-px -mr-px grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
        {stat("Realized P&L", fmtSigned(stats.trading.realized_pnl), undefined, pnlClass(stats.trading.realized_pnl))}
        {stat(
          <span title="Annualized internal return: realized P&L per capital-year of deployed entry notional. Excludes fees and funding.">
            Annual return
          </span>,
          stats.capital.air != null ? fmtPct(stats.capital.air) : "",
          "excl. fees & funding"
        )}
        {stat("Trades", stats.trading.trades, `win rate ${fmtPct(stats.trading.win_rate)}`)}
        {stat("Profit factor", stats.trading.profit_factor != null ? stats.trading.profit_factor.toFixed(2) : "")}
        {stat(
          "Slippage",
          stats.health.slippage.count > 0 ? `${stats.health.slippage.mean_bps?.toFixed(1)} bps` : "",
          stats.health.slippage.count > 0 ? `worst ${stats.health.slippage.worst_bps?.toFixed(1)} bps` : undefined
        )}
        {stat(
          "Open positions",
          stats.capital.open.length,
          stats.capital.open.length
            ? stats.capital.open
                .slice(0, 2)
                .map((p) => `${p.symbol} ${(p.side || "LONG").toLowerCase()}`)
                .join(", ") + (stats.capital.open.length > 2 ? ` +${stats.capital.open.length - 2}` : "")
            : undefined
        )}
      </div>
      {errorStats.length > 0 && (
        <div className="border-t border-[var(--divider)] px-3.5 py-2">
          <button
            type="button"
            className="text-xs text-[var(--primary)]"
            onClick={() => setShowErrors((v) => !v)}
          >
            {showErrors ? "Hide" : "Show"} why orders failed or were skipped ({errorStats.length})
          </button>
          {showErrors && (
            <div className="mt-2 max-h-56 overflow-auto">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr>
                    <th className={th}>Kind</th>
                    <th className={th}>Reason</th>
                    <th className={`${th} text-right`}>Count</th>
                    <th className={`${th} text-right`}>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {errorStats.map((r) => (
                    <tr key={`${r.kind}-${r.reason}`}>
                      <td className={td}>
                        <Pill tone={r.kind === "skipped" ? "warn" : "bad"}>{r.kind === "skipped" ? "Skipped" : "Error"}</Pill>
                      </td>
                      <td className={`${td} whitespace-normal break-all text-[var(--text-secondary)]`}>{r.reason}</td>
                      <td className={`${td} num text-right`}>{r.count}</td>
                      <td className={`${td} num text-right`}>
                        {fmtPct(r.count / (stats.health.counts.skipped + stats.health.counts.error || 1))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  ) : statsError ? (
    <div className="rounded-[10px] bg-[var(--danger-bg)] px-3 py-2 text-[13px] text-[var(--danger)]">{statsError}</div>
  ) : null;

  const showBacktest = !fixedBacktestId;
  /** Time inside a day group is just the clock; inside a week it needs the date too. */
  const clock = (iso: string | null) => {
    if (!iso) return "";
    if (period !== "day") return dateTimeShort(iso);
    const d = new Date(iso);
    const pad = (x: number) => String(x).padStart(2, "0");
    return utc ? `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}` : `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const accountCell = (accountId: string) => {
    const account = accountById.get(accountId);
    return (
      <td className={`${td} text-[var(--text-secondary)]`}>
        <span className="inline-flex items-center gap-2">
          {account && <ExchangeMark provider={account.provider} size={18} />}
          {account?.name ?? accountId}
        </span>
      </td>
    );
  };
  const strategyCell = (backtestId: string | null | undefined) => {
    const st = strategyLabel(backtestId);
    return (
      <td className={`${td} max-w-[200px] truncate ${st.muted ? "text-[var(--text-disabled)]" : "text-[var(--text-secondary)]"}`}>{st.text}</td>
    );
  };
  const hrefOf = (backtestId: string | null | undefined) =>
    liveTrades && backtestId && liveIds.has(backtestId) ? `/live/${backtestId}` : "";
  const emptyBox = (text: string) => <div className="px-4 py-10 text-center text-[13px] text-[var(--text-muted)]">{text}</div>;
  const anyFilter = Boolean(statusFilter || backtestFilter || accountFilter || query || symbolFilter || tripChip);

  // Kept short, like the Trades page: price, value, status and why are in the order's details.
  const orderCols: { h: string; right?: boolean }[] = [
    { h: "Time" },
    ...(userScope ? [{ h: "Account" }] : []),
    ...(showBacktest ? [{ h: "Strategy" }] : []),
    { h: "Symbol" },
    { h: "Action" },
    { h: "Qty", right: true },
    { h: "P&L", right: true },
  ];
  const head = (cols: { h: string; right?: boolean }[]) => (
    <thead>
      <tr>
        {cols.map((c) => (
          <th key={c.h} scope="col" className={`${th} ${c.right ? "text-right" : ""}`}>
            {c.h}
          </th>
        ))}
      </tr>
    </thead>
  );

  const orderGroups =
    period === "none" ? [{ key: "all", label: "", rows: pageOrders }] : groupByPeriod(pageOrders, (o) => o.created_at, period, utc, now);
  const ordersTable =
    filtered.length === 0 && !loading ? (
      emptyBox(anyFilter ? "Nothing matches these filters." : "No orders yet. Live strategies record every order here.")
    ) : (
      <table className="w-full border-collapse text-[13px]">
        {head(orderCols)}
        <tbody>
          {orderGroups.map((g) => (
            <FragmentRows key={g.key}>
              {period !== "none" && (
                <tr>
                  <td colSpan={orderCols.length} className={dayRow}>
                    <b className="mr-2 font-semibold text-[var(--text-secondary)]">{g.label}</b>
                    {g.rows.length} {g.rows.length === 1 ? "order" : "orders"}
                  </td>
                </tr>
              )}
              {g.rows.map((order: OrderRecord) => {
                const stale = pendingAge(order, now);
                // Filled is the norm and goes unsaid; anything else is called out next to the action.
                const pill = stale
                  ? { tone: "warn" as Tone, label: `Not confirmed · ${stale}` }
                  : order.status === "done"
                    ? null
                    : STATUS_PILL[order.status] || { tone: "mute" as Tone, label: order.status };
                const failed = order.status === "error" || order.status === "skipped";
                const pnl = pnlByOrder.get(order.id);
                const open = () => setOpenId(order.id);
                return (
                  <tr
                    key={order.id}
                    className="cursor-pointer hover:bg-[var(--bg-hover)]"
                    tabIndex={0}
                    aria-label={`Order details: ${orderLabel(order)} ${order.symbol}`}
                    onClick={open}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        open();
                      }
                    }}
                  >
                    <td className={`${td} num text-[var(--text-muted)]`} title={dateTime(order.created_at)}>
                      {clock(order.created_at)}
                    </td>
                    {userScope && accountCell(order.account_id)}
                    {showBacktest && strategyCell(order.backtest_id)}
                    <td className={`${td} font-medium text-[var(--text-primary)]`}>{order.symbol}</td>
                    <td className={td}>
                      <span className="inline-flex flex-wrap items-center gap-1.5 text-[var(--text-primary)]">
                        {orderLabel(order)}
                        {pill && <Pill tone={pill.tone}>{pill.label}</Pill>}
                      </span>
                      {failed && order.error && <div className="mt-0.5 max-w-[320px] truncate text-xs text-[var(--danger)]">{humanError(order.error)}</div>}
                    </td>
                    <td className={`${td} num text-right`} title={num(qtyOf(order), 8)}>
                      {approxQty(qtyOf(order))}
                    </td>
                    <td className={`${td} num text-right`} style={{ color: orderPnlColor(pnl) }} title={pnl === undefined ? "Opened a position" : "Without fees and funding"}>
                      {pnl == null ? "—" : signedUsd(pnl)}
                    </td>
                  </tr>
                );
              })}
            </FragmentRows>
          ))}
        </tbody>
      </table>
    );

  const tripCols: { h: string; right?: boolean }[] = [
    { h: "Closed" },
    ...(userScope ? [{ h: "Account" }] : []),
    ...(showBacktest ? [{ h: "Strategy" }] : []),
    { h: "Symbol" },
    { h: "Side" },
    { h: "Qty", right: true },
    { h: "Entry", right: true },
    { h: "Exit", right: true },
    { h: "P&L", right: true },
    { h: "Ended by" },
    { h: "Held", right: true },
  ];
  const tripGroups =
    period === "none"
      ? [{ key: "all", label: "", rows: pageTrips }]
      : groupByPeriod(pageTrips, (t) => t.exitTime || t.entryTime, period, utc, now);
  const tripsTable =
    shownTrips.length === 0 && !loading ? (
      emptyBox(anyFilter ? "Nothing matches these filters." : "No trades yet. A trade appears here when a live strategy's order fills.")
    ) : (
      <table className="w-full border-collapse text-[13px]">
        {head(tripCols)}
        <tbody>
          {tripGroups.map((g) => {
            const closed = g.rows.filter((t) => !t.open && t.pnl != null);
            const sum = closed.reduce((acc, t) => acc + (t.pnl || 0), 0);
            return (
              <FragmentRows key={g.key}>
                {period !== "none" && (
                <tr>
                  <td colSpan={tripCols.length} className={dayRow}>
                    <b className="mr-2 font-semibold text-[var(--text-secondary)]">{g.label}</b>
                    {closed.length > 0 && (
                      <>
                        <span className="num" style={{ color: pnlColor(sum) }}>
                          {money2(sum)}
                        </span>
                        {" · "}
                      </>
                    )}
                    {g.rows.length} {g.rows.length === 1 ? "trade" : "trades"}
                  </td>
                </tr>
                )}
                {g.rows.map((t) => {
                  const href = hrefOf(t.backtestId);
                  // An open trade's result so far, at its position's mark price.
                  const live = t.open ? unrealizedTripPnl(t, api.positionsByAccount?.[t.accountId]) : null;
                  return (
                    <tr
                      key={t.id}
                      className={`hover:bg-[var(--bg-hover)] ${href ? "cursor-pointer" : ""}`}
                      onClick={href ? () => navigate(href) : undefined}
                    >
                      <td className={`${td} num text-[var(--text-muted)]`} title={t.exitTime ? dateTime(t.exitTime) : `Opened ${dateTime(t.entryTime)}`}>
                        {t.open ? "" : clock(t.exitTime)}
                      </td>
                      {userScope && accountCell(t.accountId)}
                      {showBacktest && strategyCell(t.backtestId)}
                      <td className={`${td} font-medium text-[var(--text-primary)]`}>{t.symbol}</td>
                      <td className={td} style={{ color: t.side === "long" ? "var(--success)" : "var(--danger)" }}>
                        {t.side === "long" ? "Long" : "Short"}
                      </td>
                      <td className={`${td} num text-right`}>{fmt(String(t.qty))}</td>
                      <td
                        className={`${td} num text-right`}
                        title={t.entryPrice == null ? "The entry is older than the orders loaded. Load more to see it." : dateTime(t.entryTime)}
                      >
                        {t.entryPrice != null ? fmt(String(t.entryPrice), 4) : "—"}
                      </td>
                      <td className={`${td} num text-right`}>{t.exitPrice != null ? fmt(String(t.exitPrice), 4) : "—"}</td>
                      <td className={`${td} num text-right`} style={{ color: pnlColor(t.pnl ?? live?.pnl) }} title="Without fees and funding">
                        {t.pnl != null ? (
                          <>
                            {money2(t.pnl)}
                            {t.pnlPct != null && <span className="ml-1.5 text-[11px] opacity-75">{`${t.pnlPct > 0 ? "+" : ""}${t.pnlPct.toFixed(1)}%`}</span>}
                          </>
                        ) : live ? (
                          <span title="Unrealized, at the current mark price; without fees and funding">
                            {money2(live.pnl)}
                            <span className="ml-1.5 text-[11px] opacity-75">{`${live.pnlPct > 0 ? "+" : ""}${live.pnlPct.toFixed(1)}%`}</span>
                            <div className="text-[11px] text-[var(--text-muted)]">unrealized</div>
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className={td}>
                        {t.open ? <Pill tone="info">Open</Pill> : t.exitReason ? <span className={tag}>{t.exitReason}</span> : null}
                      </td>
                      <td className={`${td} num text-right text-[var(--text-muted)]`}>{held(t.entryTime, t.exitTime, now)}</td>
                    </tr>
                  );
                })}
              </FragmentRows>
            );
          })}
        </tbody>
      </table>
    );

  const table = (
    <div className="overflow-x-auto">
      {view === "trades" ? tripsTable : ordersTable}
      <Pager className="px-3 py-2.5" page={safePage} pageSize={ROWS} total={listed} onPage={setPage} />
      {liveTrades && orders.length >= limit && (
        <div className="flex items-center justify-between gap-2 px-3 py-2.5 text-xs text-[var(--text-muted)]">
          <span>
            {view === "trades" ? `Trades from the latest ${orders.length} orders` : `Showing the latest ${orders.length} orders`}
          </span>
          <button type="button" className="btn btn-ghost btn-sm" disabled={loading} onClick={() => setLimit((n) => n + PAGE)}>
            {loading ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );

  const tripChips = (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Trade result">
      {(
        [
          ["", "All"],
          ["win", "Winners"],
          ["loss", "Losers"],
          ["open", "Open"],
        ] as const
      ).map(([chip, label]) => {
        const count = tripsInScope.filter((t) => tripMatches(t, chip)).length;
        if (chip && chip !== tripChip && count === 0) return null;
        return (
          <button key={chip || "all"} type="button" aria-pressed={tripChip === chip} onClick={() => setTripChip(chip)} className={chipCls(tripChip === chip)}>
            {label} <span className="num">{count}</span>
          </button>
        );
      })}
    </div>
  );

  const viewToggle = (
    <div className="flex flex-wrap items-center gap-2">
      {liveTrades && (
        <div className="flex rounded-[9px] border border-[var(--border)] bg-[var(--bg-secondary)] p-0.5" role="group" aria-label="View">
          {(["trades", "orders"] as const).map((v) => (
            <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)} className={seg(view === v)}>
              {v === "trades" ? "Trades" : "Orders"}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-[var(--text-muted)]" id="ledger-group-label">
          Group by
        </span>
        <div
          className="flex rounded-[9px] border border-[var(--border)] bg-[var(--bg-secondary)] p-0.5"
          role="group"
          aria-labelledby="ledger-group-label"
          title="Split the list into days or weeks, each with its own total"
        >
          {(
            [
              ["none", "None"],
              ["day", "Days"],
              ["week", "Weeks"],
            ] as const
          ).map(([p, label]) => (
            <button key={p} type="button" aria-pressed={period === p} onClick={() => setPeriod(p)} className={seg(period === p)}>
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const symbolCards =
    liveTrades && stats && stats.groups.length > 0 ? (
      <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-2" role="group" aria-label="Filter by symbol">
        {[...stats.groups]
          .sort((a, b) => Math.abs(Number(b.realized_pnl)) - Math.abs(Number(a.realized_pnl)))
          .map((g) => {
            const on = symbolFilter === g.symbol;
            const pnl = Number(g.realized_pnl);
            return (
              <button
                key={g.symbol}
                type="button"
                aria-pressed={on}
                onClick={() => setSymbolFilter(on ? "" : g.symbol)}
                className={`flex flex-col gap-0.5 rounded-[10px] border bg-[var(--bg-card-elevated)] px-3 py-2 text-left hover:bg-[var(--bg-hover)] ${
                  on ? "border-[var(--primary)] shadow-[inset_0_0_0_1px_var(--primary)]" : "border-[var(--border)]"
                }`}
              >
                <b className="text-[13px] font-semibold text-[var(--text-primary)]">{g.symbol}</b>
                <span className="num text-[15px] font-semibold" style={{ color: pnlColor(Number.isFinite(pnl) ? pnl : null) }}>
                  {g.trades > 0 && Number.isFinite(pnl) ? money2(pnl) : "—"}
                </span>
                <span className="text-[11px] text-[var(--text-muted)]">
                  {g.trades} {g.trades === 1 ? "trade" : "trades"}
                  {g.trades > 0 ? ` · win ${fmtPct(g.win_rate, 0)}` : ""}
                </span>
              </button>
            );
          })}
      </div>
    ) : null;

  const problemsBanner =
    liveTrades && recentProblems.count > 0 ? (
      <div className="flex flex-wrap items-center gap-3 rounded-[12px] border border-[color-mix(in_srgb,var(--danger)_35%,var(--border))] bg-[color-mix(in_srgb,var(--danger)_7%,transparent)] px-3.5 py-2.5">
        <span className="w-1 self-stretch rounded bg-[var(--danger)]" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-[var(--text-primary)]">
            {recentProblems.count} {recentProblems.count === 1 ? "order" : "orders"} failed or {recentProblems.count === 1 ? "was" : "were"} skipped in the last 7 days
          </div>
          <div className="truncate text-xs text-[var(--text-muted)]">
            {recentProblems.reasons.map(([why, c]) => `${why} (${c})`).join(" · ")}
          </div>
        </div>
        <button
          type="button"
          className="btn btn-soft btn-sm"
          aria-pressed={showingProblems}
          onClick={() => {
            if (showingProblems) {
              // Back to where the user was before "Show them".
              setView(beforeProblems.current.view);
              setStatusFilter(beforeProblems.current.status);
              return;
            }
            beforeProblems.current = { view, status: statusFilter };
            setView("orders");
            setStatusFilter("problems");
          }}
        >
          {showingProblems ? "Hide them" : "Show them"}
        </button>
      </div>
    ) : null;

  const accountChips = liveTrades ? (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Account">
      {[{ id: "", name: "All accounts", provider: "" }, ...api.accounts].map((a: { id: string; name: string; provider: string }) => {
        const on = accountFilter === a.id;
        return (
          <button
            key={a.id || "all"}
            type="button"
            aria-pressed={on}
            onClick={() => {
              setLimit(PAGE);
              setAccountFilter(a.id);
            }}
            className={`inline-flex items-center gap-1.5 rounded-full border border-[var(--border)] px-2.5 py-[3px] text-xs ${
              on ? "bg-[var(--bg-card-elevated)] text-[var(--text-primary)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            }`}
          >
            {a.provider && <ExchangeMark provider={a.provider} size={16} />}
            {a.name}
            {accountCounts[a.id] !== undefined && <span className="num">{accountCounts[a.id]}</span>}
          </button>
        );
      })}
    </div>
  ) : null;

  const filters = (
    <div className="flex flex-wrap items-center gap-2">
      {liveTrades && (
        <label className="flex min-w-[200px] items-center gap-2 rounded-[10px] border border-[var(--border)] bg-[var(--bg-secondary)] px-2.5 py-1 text-xs text-[var(--text-disabled)] focus-within:border-[var(--primary)]">
          <Search size={13} className="shrink-0" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Strategy or symbol"
            aria-label="Search trades"
            className="!h-auto !min-h-0 w-full !border-0 !bg-transparent !p-0 !text-xs text-[var(--text-primary)] !shadow-none !outline-none placeholder:text-[var(--text-disabled)]"
          />
        </label>
      )}
      {userScope && !liveTrades && (
        <select
          value={accountFilter}
          onChange={(e) => setAccountFilter(e.target.value)}
          className="!h-auto !min-h-0 !w-auto !py-1 !text-xs"
          aria-label="Account"
        >
          <option value="">All accounts</option>
          {api.accounts.map((acct: { id: string; name: string }) => (
            <option key={acct.id} value={acct.id}>
              {acct.name}
            </option>
          ))}
        </select>
      )}
      {!fixedBacktestId && !compact && (
        <select
          value={backtestFilter}
          onChange={(e) => setBacktestFilter(e.target.value)}
          className="!h-auto !min-h-0 !w-auto !py-1 !text-xs"
          aria-label="Strategy"
        >
          <option value="">All strategies</option>
          <option value="none">Manual (no strategy)</option>
          {backtests.map((bt) => (
            <option key={bt.id} value={bt.id}>
              {bt.name}
            </option>
          ))}
        </select>
      )}
      {!compact && (
        <button type="button" className="btn btn-ghost btn-sm" disabled={loading} onClick={refresh}>
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      )}
    </div>
  );

  // On a strategy's or an exchange's page the list stays plain: two facts in a line, no chips or grouping.
  const summary =
    compact && stats ? (
      <p className="m-0 text-[13px] text-[var(--text-muted)]">
        Realized P&amp;L{" "}
        <span className={`num font-semibold ${pnlClass(stats.trading.realized_pnl)}`}>{fmtSigned(stats.trading.realized_pnl)}</span>
        {stats.health.slippage.count > 0 && (
          <>
            {" · "}Slippage <span className="num text-[var(--text-primary)]">{stats.health.slippage.mean_bps?.toFixed(1)} bps</span> on average,
            worst <span className="num">{stats.health.slippage.worst_bps?.toFixed(1)} bps</span>
          </>
        )}
      </p>
    ) : null;

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {accountChips}
      {!compact && statsStrip}
      {symbolCards}
      {problemsBanner}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          {title !== "" && compact && <h2 className="m-0 text-[15px] font-semibold text-[var(--text-primary)]">{title || "Orders"}</h2>}
          {compact ? summary : viewToggle}
          {!compact && (view === "trades" ? tripChips : chips)}
        </div>
        {filters}
      </div>
      {error && <div className="rounded-[10px] bg-[var(--danger-bg)] px-3 py-2 text-[13px] text-[var(--danger)]">{error}</div>}
      {table}
      {openId && (() => {
        const o = orders.find((r) => r.id === openId);
        if (!o) return null;
        return (
          <OrderDetailsDialog
            order={o}
            account={accountById.get(o.account_id) as Account | undefined}
            strategy={(api.backtests as Backtest[]).find((b) => b.id === o.backtest_id)}
            pnl={pnlByOrder.get(o.id)}
            onClose={() => setOpenId(null)}
          />
        );
      })()}
    </div>
  );
}
